import { COVERAGE_BETA_MAX_IMAGE_ZOOM } from "@/config/coverage-beta-zoom";
import {
  ReferenceImageMatch,
  SampleOosPrediction,
} from "@/dev/sample-out-of-stock-predictions";
import { useFetchGalleryImgUrl } from "@/hooks/useFetchGalleryImgUrl";
import { useFocusActiveQaItem } from "@/hooks/useFocusActiveQaItem";
import {
  taskParcelsQueryOptions,
  useRelatedFramesEnabled,
  useTaskRelatedFrames,
} from "@/hooks/useTaskRelatedFrames";
import { neighbourSpots as toNeighbourSpots } from "@/utils/nearest-product-ids";
import { useQuery } from "@tanstack/react-query";
import { captureImageLoad } from "@/utils/captureImageLoad";
import { setBoxOutlineZoom } from "@/utils/coverage-beta-box-outline";
import {
  isOnActiveView,
  useCoverageBetaStore,
} from "@/store/useCoverageBetaStore";
import { focusEmptyAreaBox } from "@/utils/coverage-beta-focus";
import { usePostHog } from "@posthog/react";
import { getBoundingBoxStyle } from "@/utils/utils";
import { OoSParcelSchema } from "@augmodo/lebron-browser-client";
import { IconSun } from "@tabler/icons-react";
import { TransformComponent, TransformWrapper } from "react-zoom-pan-pinch";
import {
  QueryEmptyAreaDetectionBox,
  RESHAPABLE_BOX_CLASS,
} from "../QueryEmptyAreaDetectionBox";
import { QueryProductDetectionBox } from "../QueryProductDetectionBox";
import { QueryPersonBox } from "./query-person-box/QueryPersonBox";
import { FrameKindChip, frameKind } from "./FrameKindChip";
import { FrameScrubber } from "./FrameScrubber";
import { Sam2BoxesToggle } from "./Sam2BoxesToggle";
import { QueryViewFilmstrip } from "./QueryViewFilmstrip";
import {
  PropagatedAreaOverlay,
  usePropagatedAreaItems,
} from "./PropagatedAreaOverlay";
import { values } from "lodash";
import { useShallow } from "zustand/react/shallow";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent,
} from "react";

// Minimum box size (normalized) below which a drag is ignored as a stray click.
const MIN_BOX_SIZE = 0.01;

type NormPoint = [number, number];

const clamp01 = (v: number) => Math.max(0, Math.min(1, v));

const formatOffset = (offsetMs: number) => {
  const s = offsetMs / 1000;
  const abs = Math.abs(s);
  const value = abs < 10 ? abs.toFixed(1) : Math.round(abs).toString();
  return `${s < 0 ? "−" : "+"}${value}s`;
};

interface Props {
  jobId: string;
  taskId: string;
  parcelResp: OoSParcelSchema | undefined;
  out_of_stock_predictions: SampleOosPrediction[];
  /**
   * A benchmark task's parcel carries the labels its gold answer was built
   * from — show none of them (no related frames, no known-area hints).
   */
  isBenchmarkTask?: boolean;
  /**
   * Fired once the query image has settled — its bytes have painted, or it
   * failed to load (so a broken image can't hold warm-up forever) — the signal CoverageBetaTask uses to unlock the
   * planogram view, so its product-image requests don't compete with this
   * one for bandwidth. See CoverageBetaPlanogramView's `isWarmupReady` prop.
   */
  onImageLoaded?: () => void;
  /**
   * The admin view: the parcels are read with SAM 2's guesses, which a toggle
   * draws next to the labelers' boxes.
   */
  isAdmin?: boolean;
}
export const QueryImageRelated = (props: Props) => {
  const {
    jobId,
    taskId,
    parcelResp,
    out_of_stock_predictions,
    isBenchmarkTask = false,
    onImageLoaded,
    isAdmin = false,
  } = props;
  const query_image_id = parcelResp?.query?.image_id ?? "";
  // Grouped query images: which view is shown. Boxes are in their own view's
  // coordinates, so only the shown view's boxes render; the model's product
  // detections (and related frames) exist only for the primary.
  const activeImageId = useCoverageBetaStore((s) => s.activeImageId);
  const shownImageId = activeImageId ?? query_image_id;
  const isPrimaryView = shownImageId === query_image_id;
  const viewNumber = useCoverageBetaStore(
    (s) =>
      s.groupImages.find((image) => image.imageId === shownImageId)?.viewNumber,
  );
  const onShownView = (box: { imageId?: string | null }) =>
    isOnActiveView(box, { activeImageId, primaryImageId: query_image_id });

  // console.log("out_of_stock_predictions: ", out_of_stock_predictions);

  const emptyAreaIdxToReferenceMatches = out_of_stock_predictions.reduce<{
    [idx: number]: ReferenceImageMatch[];
  }>((acc, p) => {
    if (!acc[p.empty_area_index]) acc[p.empty_area_index] = [];
    acc[p.empty_area_index].push(...p.matches_products_reference_images);
    return acc;
  }, {});

  const queryProductDetections = isPrimaryView
    ? (parcelResp?.query?.product_detections ?? [])
    : [];
  const queryProductIds = isPrimaryView
    ? (parcelResp?.query?.product_ids ?? [])
    : [];

  const { data: queryImgResp } = useFetchGalleryImgUrl(shownImageId);
  const queryImageObjUrl = queryImgResp?.objUrl || "";
  // Which query image URL has actually finished loading in the DOM — tracked
  // by URL (not a boolean) so a task-to-task transition invalidates readiness
  // for free once the new image lands. Used to gate QA-pager focusing below:
  // the wrapper around the boxes has zero size until the <img> paints, so
  // focusing any earlier would measure a zero-size element.
  const [loadedImgUrl, setLoadedImgUrl] = useState("");

  const feEmptyAreas = useCoverageBetaStore(
    useShallow((s) => values(s.idxToFeEmptyArea)),
  );
  const persons = useCoverageBetaStore((s) => s.persons);
  const isQaMode = useCoverageBetaStore((s) => s.isQaMode);
  const activeMode = useCoverageBetaStore((s) => s.activeMode);
  const setPinchRef = useCoverageBetaStore((s) => s.setPinchRef);
  const showBoxes = useCoverageBetaStore((s) => s.showBoxes);
  const setHoveredView = useCoverageBetaStore((s) => s.setHoveredView);
  const isReadOnly = useCoverageBetaStore((s) => s.isReadOnly);
  const brightness = useCoverageBetaStore((s) => s.brightnessByView.query);
  const posthog = usePostHog();

  // The task's context parcels — other views of the same shelf captured around
  // the same time — as a scrubbable frame sequence. null → no scrubber.
  // `frameIdx` null means "the task's own (primary) frame".
  const relatedFramesEnabled = useRelatedFramesEnabled() && !isBenchmarkTask;
  const taskFrames = useTaskRelatedFrames(
    jobId,
    taskId,
    relatedFramesEnabled,
    isAdmin,
  );
  // The same parcels list (shared cache) — the labeled/propagated areas known
  // for whichever image is shown, primary or a similar view. Ignored when
  // disabled: a disabled query still returns what the next-task prefetch cached.
  const { data: cachedTaskParcels } = useQuery({
    ...taskParcelsQueryOptions(jobId, taskId, { includeSam2: isAdmin }),
    enabled: relatedFramesEnabled && !!jobId && !!taskId,
  });
  const taskParcels = relatedFramesEnabled ? cachedTaskParcels : undefined;
  const shownParcel =
    taskParcels?.find((p) => p.image_id === shownImageId) ?? null;
  // What an empty area's hover localizes against — see neighbourSpots.
  const neighbourSpots = toNeighbourSpots(
    queryProductDetections,
    queryProductIds,
    shownParcel?.annotations ?? [],
  );
  // Related frames are around the primary capture — not shown on other views.
  const nearbyFrames = isPrimaryView ? taskFrames : null;
  const queryFrameIdx = Math.max(
    0,
    nearbyFrames?.findIndex((f) => f.role === "PRIMARY") ?? 0,
  );
  const [frameIdx, setFrameIdx] = useState<number | null>(null);
  useEffect(() => setFrameIdx(null), [taskId]);
  const activeFrameIdx = frameIdx ?? queryFrameIdx;
  const contextFrame =
    nearbyFrames && activeFrameIdx !== queryFrameIdx
      ? nearbyFrames[activeFrameIdx]
      : null;
  // While the selected frame is still downloading, keep the last frame that
  // did load on screen instead of a blank panel.
  const lastShownFrameRef = useRef<typeof contextFrame>(null);
  if (!contextFrame) lastShownFrameRef.current = null;
  else if (contextFrame.status === "loaded")
    lastShownFrameRef.current = contextFrame;
  const shownContextFrame = lastShownFrameRef.current;
  // Boxes are in the task frame's coordinates — hide them on any other frame.
  const showQueryBoxes = showBoxes && !contextFrame;

  // Natural sizes, to place propagated boxes on letterboxed context frames.
  const [naturalSizeByUrl, setNaturalSizeByUrl] = useState<
    Record<string, { width: number; height: number }>
  >({});
  const recordNaturalSize = (url: string, img: HTMLImageElement) =>
    setNaturalSizeByUrl((prev) =>
      prev[url]
        ? prev
        : {
            ...prev,
            [url]: { width: img.naturalWidth, height: img.naturalHeight },
          },
    );

  // Areas to draw over a context frame, or over a similar view (labeling stays
  // on the primary, so a similar view shows what's known there instead).
  const overlayParcel = contextFrame ?? (isPrimaryView ? null : shownParcel);
  const propagatedItems = usePropagatedAreaItems(
    taskParcels ?? null,
    overlayParcel,
  );
  // In the admin view SAM 2's guesses are drawn only by its own layer (below,
  // behind the toggle), so this one keeps to the labelers' boxes.
  const labeledItems = isAdmin
    ? propagatedItems.filter((item) => item.isHuman)
    : propagatedItems;

  // Admin view: SAM 2's guesses for the frame shown (the task's own frame, a
  // similar view or a context frame), including ones labeled since and ones
  // the labeler UI hides — see TaskParcel.sam2_annotations.
  const showSam2Boxes = useCoverageBetaStore((s) => s.showSam2Boxes);
  const sam2Parcels = useMemo(
    () =>
      (taskParcels ?? []).map((p) => ({
        parcel_id: p.parcel_id,
        annotations: p.sam2_annotations ?? [],
      })),
    [taskParcels],
  );
  const sam2TargetId = (contextFrame ?? shownParcel)?.parcel_id;
  const sam2Items = usePropagatedAreaItems(
    sam2Parcels,
    sam2Parcels.find((p) => p.parcel_id === sam2TargetId) ?? null,
  );
  const hasSam2Guesses =
    isAdmin && sam2Parcels.some((p) => p.annotations.length > 0);
  const drawSam2 = hasSam2Guesses && showBoxes && showSam2Boxes;

  // A parcel's areas are either all a labeler's or all SAM2's (see lebron).
  const labelStatus = propagatedItems.length
    ? `${propagatedItems.length} ${propagatedItems[0].isHuman ? "labeled" : "propagated"}`
    : "no labels yet";

  const isBoxMode = activeMode === "draw-box";
  const isPersonMode = activeMode === "draw-person";
  const isDrawingMode = isBoxMode || isPersonMode;

  // Always frame the QA pager's current item — see useFocusActiveQaItem.
  useFocusActiveQaItem(!!queryImageObjUrl && loadedImgUrl === queryImageObjUrl);

  // A different view or frame is a different image — start it un-zoomed.
  useEffect(() => {
    useCoverageBetaStore.getState().pinchRef?.resetTransform(0);
  }, [shownImageId, activeFrameIdx]);

  // An area picked from the sidebar / hotkeys while on another view switched
  // the view; frame it once the new image has painted and its box can be
  // measured.
  useEffect(() => {
    if (!queryImageObjUrl || loadedImgUrl !== queryImageObjUrl) return;
    const { pendingAreaFocusIdx, clearPendingAreaFocus } =
      useCoverageBetaStore.getState();
    if (pendingAreaFocusIdx == null) return;
    clearPendingAreaFocus();
    const raf = requestAnimationFrame(() =>
      focusEmptyAreaBox(pendingAreaFocusIdx),
    );
    return () => cancelAnimationFrame(raf);
  }, [loadedImgUrl, queryImageObjUrl]);

  // Carries the zoom scale for the box outlines. The view root, not the image
  // wrapper below, so the scale survives the image swap between tasks.
  const viewRef = useRef<HTMLDivElement>(null);

  // The div that tightly wraps the <img>. Because it lives inside
  // TransformComponent, its bounding rect already reflects the current
  // zoom/pan, so we can normalize pointer coords straight against it.
  const imageWrapperRef = useRef<HTMLDivElement>(null);
  const [drawStart, setDrawStart] = useState<NormPoint | null>(null);
  const [drawEnd, setDrawEnd] = useState<NormPoint | null>(null);
  const isDrawing = drawStart !== null;

  // Refs mirror the draw points so the window-level drag listeners always read
  // the latest values without having to re-subscribe on every mouse move.
  const drawStartRef = useRef<NormPoint | null>(null);
  const drawEndRef = useRef<NormPoint | null>(null);
  // The mode captured when the drag started, so a mid-drag mode switch can't
  // change which shape (empty area vs person) the box commits as.
  const drawModeRef = useRef<"draw-box" | "draw-person">("draw-box");

  const getNormalizedPoint = useCallback(
    (clientX: number, clientY: number): NormPoint | null => {
      const el = imageWrapperRef.current;
      if (!el) return null;
      const rect = el.getBoundingClientRect();
      if (!rect.width || !rect.height) return null;
      return [
        clamp01((clientX - rect.left) / rect.width),
        clamp01((clientY - rect.top) / rect.height),
      ];
    },
    [],
  );

  const clearDraw = useCallback(() => {
    drawStartRef.current = null;
    drawEndRef.current = null;
    setDrawStart(null);
    setDrawEnd(null);
  }, []);

  // Leaving draw mode (e.g. via the Esc hotkey) tears down the drag listeners,
  // so clear any in-progress draw preview.
  useEffect(() => {
    if (!isDrawingMode) clearDraw();
  }, [isDrawingMode, clearDraw]);

  const handleDrawMouseDown = (e: MouseEvent) => {
    const point = getNormalizedPoint(e.clientX, e.clientY);
    if (!point) return;
    e.preventDefault();
    e.stopPropagation();
    drawStartRef.current = point;
    drawEndRef.current = point;
    drawModeRef.current = isPersonMode ? "draw-person" : "draw-box";
    setDrawStart(point);
    setDrawEnd(point);
  };

  // While a drag is active, track it on the window so it continues even when
  // the cursor leaves the image; only mouse-up commits the box. Coordinates are
  // clamped to the image by getNormalizedPoint.
  useEffect(() => {
    if (!isDrawing) return;

    const handleMove = (e: globalThis.MouseEvent) => {
      const point = getNormalizedPoint(e.clientX, e.clientY);
      if (!point) return;
      drawEndRef.current = point;
      setDrawEnd(point);
    };

    const handleUp = () => {
      const start = drawStartRef.current;
      const end = drawEndRef.current;
      clearDraw();
      if (!start || !end) return;

      const minX = Math.min(start[0], end[0]);
      const maxX = Math.max(start[0], end[0]);
      const minY = Math.min(start[1], end[1]);
      const maxY = Math.max(start[1], end[1]);
      if (maxX - minX <= MIN_BOX_SIZE || maxY - minY <= MIN_BOX_SIZE) return;

      // 4 corners, top-left → top-right → bottom-right → bottom-left.
      const points: number[][] = [
        [minX, minY],
        [maxX, minY],
        [maxX, maxY],
        [minX, maxY],
      ];
      // Commit as the mode the drag STARTED in (captured at mouse-down), not
      // whatever the mode happens to be at mouse-up.
      const { addPerson, addFeEmptyArea } = useCoverageBetaStore.getState();
      if (drawModeRef.current === "draw-person") addPerson(points);
      else addFeEmptyArea(points);
    };

    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);
    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
    };
  }, [isDrawing, getNormalizedPoint, clearDraw]);

  return (
    <div
      ref={viewRef}
      className="size-full flex flex-col bg-gray-300"
      onMouseEnter={() => setHoveredView("query")}
    >
      <div className="relative flex-1 min-h-0">
        {!isPrimaryView && viewNumber != null && (
          <div className="absolute left-3 top-3 z-10 rounded-md border border-gray-300 bg-gray-100/95 px-2 py-1 text-xs font-semibold text-gray-700 shadow-md">
            Similar view {viewNumber} · view only
          </div>
        )}
        {hasSam2Guesses && (
          <div className="absolute right-3 top-3 z-10">
            <Sam2BoxesToggle count={sam2Items.length} />
          </div>
        )}
        {brightness !== 100 && (
          <div className="absolute bottom-3 left-3 z-10 flex items-center gap-1.5 rounded-md border border-gray-300 bg-gray-100/95 px-2 py-1 text-xs font-semibold text-gray-700 shadow-md">
            <IconSun size={14} stroke={2.5} />
            {brightness}%
          </div>
        )}
        {nearbyFrames && (
          <div className="absolute left-3 top-3 z-10 flex items-center gap-1.5 rounded-md border border-gray-300 bg-gray-100/95 px-2 py-1 text-xs font-semibold text-gray-700 shadow-md">
            <FrameKindChip
              kind={frameKind(
                nearbyFrames[activeFrameIdx],
                activeFrameIdx === queryFrameIdx,
              )}
            />
            {contextFrame
              ? `${formatOffset(contextFrame.offset_ms ?? 0)} · ${
                  contextFrame.video_id === parcelResp?.query?.video_id
                    ? "same video"
                    : "other pass"
                } · ${
                  contextFrame.status === "loading"
                    ? "loading…"
                    : contextFrame.status === "error"
                      ? "failed to load"
                      : labelStatus
                }`
              : // Served before a related task's labels finished propagating
                // onto it; usePropagatedPrefill merges them in when they land.
                nearbyFrames[queryFrameIdx].labels_pending &&
                "Pre-labels on the way…"}
          </div>
        )}
        {nearbyFrames && (
          <FrameScrubber
            frames={nearbyFrames}
            frameIdx={activeFrameIdx}
            queryIdx={queryFrameIdx}
            onChange={setFrameIdx}
          />
        )}
        <TransformWrapper
          initialScale={1}
          minScale={1}
          maxScale={COVERAGE_BETA_MAX_IMAGE_ZOOM}
          centerZoomedOut={false}
          limitToBounds={false}
          velocityAnimation={{ disabled: true }}
          doubleClick={{ disabled: true }}
          panning={{
            velocityDisabled: true,
            disabled: isDrawingMode,
            excluded: [RESHAPABLE_BOX_CLASS],
          }}
          onInit={setPinchRef}
          // Sizes the box outlines — see coverage-beta-box-outline.
          onTransformed={(_ref, state) =>
            setBoxOutlineZoom(viewRef.current, state.scale)
          }
        >
          {({ zoomToElement }) => {
            return (
              <TransformComponent
                wrapperStyle={{ width: "100%", height: "100%" }}
                contentStyle={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {!!queryImageObjUrl && (
                  <div
                    ref={imageWrapperRef}
                    className="relative inline-block h-full"
                  >
                    {/* The query image is always needed first — load it ahead of
                      the reference thumbnails on slow connections. */}
                    <img
                      src={queryImageObjUrl}
                      className="h-full w-auto"
                      style={{ filter: `brightness(${brightness}%)` }}
                      fetchpriority="high"
                      onLoad={(e) => {
                        recordNaturalSize(queryImageObjUrl, e.currentTarget);
                        setLoadedImgUrl(queryImageObjUrl);
                        onImageLoaded?.();
                        if (!isReadOnly)
                          captureImageLoad(
                            posthog,
                            "query",
                            queryImageObjUrl,
                            queryImgResp?.optimized,
                          );
                      }}
                      onError={() => onImageLoaded?.()}
                    />

                    {/* A context frame drawn over the (still-mounted) task
                      frame, so zoom/pan and load tracking are untouched. */}
                    {shownContextFrame && (
                      <img
                        src={shownContextFrame.url}
                        className="absolute inset-0 size-full bg-gray-300 object-contain"
                        style={{ filter: `brightness(${brightness}%)` }}
                        onLoad={(e) =>
                          recordNaturalSize(
                            shownContextFrame.url,
                            e.currentTarget,
                          )
                        }
                      />
                    )}

                    {showBoxes && !isPrimaryView && labeledItems.length > 0 && (
                      <PropagatedAreaOverlay
                        items={labeledItems}
                        containerSize={null}
                        frameSize={null}
                      />
                    )}

                    {showBoxes &&
                      labeledItems.length > 0 &&
                      contextFrame &&
                      shownContextFrame === contextFrame && (
                        <PropagatedAreaOverlay
                          items={labeledItems}
                          containerSize={
                            naturalSizeByUrl[queryImageObjUrl] ?? null
                          }
                          frameSize={naturalSizeByUrl[contextFrame.url] ?? null}
                        />
                      )}

                    {/* Admin: SAM 2's guesses, under the task's own boxes so
                      those stay clickable. */}
                    {drawSam2 &&
                      sam2Items.length > 0 &&
                      (contextFrame ? (
                        shownContextFrame === contextFrame && (
                          <PropagatedAreaOverlay
                            items={sam2Items}
                            containerSize={
                              naturalSizeByUrl[queryImageObjUrl] ?? null
                            }
                            frameSize={
                              naturalSizeByUrl[contextFrame.url] ?? null
                            }
                          />
                        )
                      ) : (
                        <PropagatedAreaOverlay
                          items={sam2Items}
                          containerSize={null}
                          frameSize={null}
                        />
                      ))}

                    {showQueryBoxes &&
                      queryProductDetections.map(
                        (productDetection: any, idx) => {
                          const productId = queryProductIds[idx] || "";
                          // Without a recognized product (e.g. pilot parcels)
                          // a detection can't point at the planogram — skip it.
                          if (!productId) return null;
                          return (
                            <QueryProductDetectionBox
                              key={productDetection.id}
                              productDetection={productDetection}
                              productId={productId}
                            />
                          );
                        },
                      )}

                    {showQueryBoxes &&
                      feEmptyAreas.filter(onShownView).map((feEmptyArea) => {
                        const { feEmptyAreaIdx } = feEmptyArea;
                        // Predictions are keyed by the model's area index; a
                        // drawn or propagated-in area can land on the same
                        // index, so only model areas take them.
                        const referenceMatches =
                          feEmptyArea.source === "auto"
                            ? (emptyAreaIdxToReferenceMatches[feEmptyAreaIdx] ??
                              [])
                            : [];
                        return (
                          <QueryEmptyAreaDetectionBox
                            key={feEmptyArea.id}
                            emptyAreaDetection={feEmptyArea}
                            emptyAreaIdx={feEmptyAreaIdx}
                            referenceMatches={referenceMatches}
                            zoomToElement={zoomToElement}
                            out_of_stock_predictions={out_of_stock_predictions}
                            neighbourSpots={neighbourSpots}
                            isViewOnly={!isPrimaryView}
                          />
                        );
                      })}

                    {/* Human-drawn unblurred-person boxes (drag/resize in draw
                      mode). Outside QA mode a deleted one is just gone; in QA
                      mode it still needs to render so a reviewer can inspect
                      the deletion — same treatment as deleted empty areas. */}
                    {showQueryBoxes &&
                      persons.map((person, idx) => {
                        if (person.isDeleted && !isQaMode) return null;
                        if (!onShownView(person)) return null;
                        return (
                          <QueryPersonBox
                            key={person.id}
                            person={person}
                            idx={idx}
                          />
                        );
                      })}

                    {/* Live preview of the box currently being drawn.
                      Red in person mode, orange for empty areas. */}
                    {isDrawing && drawStart && drawEnd && (
                      <div
                        className={
                          isPersonMode
                            ? "absolute border-2 border-dashed border-red-500 bg-red-500/40 pointer-events-none z-[60]"
                            : "absolute border-2 border-dashed border-orange-400 bg-orange-400/40 pointer-events-none z-[60]"
                        }
                        style={getBoundingBoxStyle([drawStart, drawEnd])}
                      />
                    )}

                    {/* Event-capture layer — only active in draw mode so that
                      cursor mode keeps box clicks and panning working. */}
                    {isDrawingMode && !contextFrame && isPrimaryView && (
                      <div
                        className="absolute inset-0 z-[50]"
                        style={{ cursor: "crosshair" }}
                        onMouseDown={handleDrawMouseDown}
                      />
                    )}
                  </div>
                )}
              </TransformComponent>
            );
          }}
        </TransformWrapper>
      </div>
      <QueryViewFilmstrip />
    </div>
  );
};
